import type { AxiosInstance, InternalAxiosRequestConfig } from "axios";
import localforage from "localforage";
import {
  clearEnterpriseAuthState,
  enterpriseSessionRevision,
  isEnterpriseSessionCurrent,
  isJWTStructurallyValid,
  refreshEnterpriseSession,
  writeEnterpriseSession,
} from "./auth";

type SessionRequest = InternalAxiosRequestConfig & {
  _retry?: boolean;
  _replayedStoredJWT?: boolean;
  _sessionRevision?: number;
};

// A request made by a previous account must never be retried with the new
// account's JWT, nor clear its credentials when a delayed 401 arrives.
export function setup401Interceptor(instance: AxiosInstance) {
  instance.interceptors.request.use((config: SessionRequest) => {
    config._sessionRevision = enterpriseSessionRevision();
    return config;
  });
  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const request = error.config as SessionRequest | undefined;
      const revision = request?._sessionRevision;
      if (
        error.response?.status !== 401 ||
        !request ||
        request._retry ||
        request.url?.includes("/auth/refresh") ||
        revision === undefined ||
        !isEnterpriseSessionCurrent(revision)
      )
        return Promise.reject(error);

      const requestJWT = request.headers.get("X-JWT");
      // Public endpoints do not own the current authenticated session.
      if (!requestJWT) return Promise.reject(error);
      const currentJWT = await localforage.getItem("JWT");
      if (!isEnterpriseSessionCurrent(revision)) return Promise.reject(error);
      // Within one session revision a different stored JWT can only mean a
      // proactive refresh of the SAME account already landed (a new account
      // always bumps the revision). Replay once with the stored JWT instead of
      // refreshing again or surfacing the 401; a second 401 falls through to
      // the refresh path below, so the request is bounded to three attempts.
      if (
        !request._replayedStoredJWT &&
        isJWTStructurallyValid(currentJWT) &&
        currentJWT !== requestJWT
      ) {
        request._replayedStoredJWT = true;
        request.headers.set("X-JWT", currentJWT);
        return instance(request);
      }
      request._retry = true;
      await writeEnterpriseSession(revision, async () => {
        await localforage.removeItem("TrustedSession");
        await localforage.removeItem("permissions");
      });
      if (!isEnterpriseSessionCurrent(revision)) return Promise.reject(error);
      const refreshed = await refreshEnterpriseSession();
      if (!isEnterpriseSessionCurrent(revision)) return Promise.reject(error);
      if (refreshed) {
        const jwt = await localforage.getItem<string>("JWT");
        if (jwt && isEnterpriseSessionCurrent(revision)) {
          request.headers.set("X-JWT", jwt);
          return instance(request);
        }
      }
      if (await clearEnterpriseAuthState(revision)) window.location.replace("/auth");
      return Promise.reject(error);
    },
  );
}
